import { useCallback, useEffect, useState } from "react";
import { Copy, Download, ExternalLink, RefreshCw } from "lucide-react";
import { userApi, type AccountSubscription } from "../lib/api";
import { clashImportUrl } from "../lib/clash";
import { useI18n } from "../i18n";

const labels = {
  ready: "可用",
  pending: "待开通",
  expired: "已到期",
  exhausted: "流量已用完",
  disabled: "已停用",
  stale: "等待节点同步",
};
const gb = (bytes: number) => `${(bytes / 1024 ** 3).toFixed(2)} GB`;
const date = (value: string | null, locale: string) =>
  value
    ? new Intl.DateTimeFormat(locale, {
        year: "numeric",
        month: "2-digit",
        day: "2-digit",
        hour: "2-digit",
        minute: "2-digit",
        hour12: false,
      }).format(new Date(value))
    : "—";

interface SubscriptionCardProps {
  subscription?: AccountSubscription | null;
  onRefresh?: () => void;
}

export function SubscriptionCard({ subscription: propSubscription, onRefresh }: SubscriptionCardProps = {}) {
  const { locale, t } = useI18n();
  const [internalSubscription, setInternalSubscription] = useState<AccountSubscription | null>(null);
  const [loading, setLoading] = useState(propSubscription === undefined);
  const [error, setError] = useState<string | null>(null);
  const [notice, setNotice] = useState("");
  const [manualCopy, setManualCopy] = useState(false);

  const subscription = propSubscription !== undefined ? propSubscription : internalSubscription;

  const refresh = useCallback(async (signal?: AbortSignal) => {
    if (propSubscription !== undefined) {
      onRefresh?.();
      return;
    }
    try {
      const result = await userApi.subscription();
      if (!signal?.aborted) {
        setInternalSubscription(result);
        setError(null);
      }
    } catch (caught) {
      if (!signal?.aborted) setError(caught instanceof Error ? caught.message : "订阅暂时不可用。");
    } finally {
      if (!signal?.aborted) setLoading(false);
    }
  }, [propSubscription, onRefresh]);

  useEffect(() => {
    if (propSubscription !== undefined) {
      setLoading(false);
      return;
    }
    const controller = new AbortController();
    void refresh(controller.signal);
    const timer = window.setInterval(() => {
      if (document.visibilityState === "visible") void refresh(controller.signal);
    }, 60_000);
    return () => {
      controller.abort();
      window.clearInterval(timer);
    };
  }, [propSubscription, refresh]);

  const copy = async () => {
    if (!subscription?.url) return;
    try {
      await navigator.clipboard.writeText(subscription.url);
      setManualCopy(false);
      setNotice("订阅链接已复制，请勿分享。");
    } catch {
      setManualCopy(true);
      setNotice("浏览器未允许复制，可选中下面的链接手动复制。");
    }
  };

  const used = (subscription?.uploadBytes ?? 0) + (subscription?.downloadBytes ?? 0);
  const canImport = subscription?.status === "ready" && Boolean(subscription.url) && !error;
  const job = subscription?.provisioning;
  const pendingCopy = job?.status === 'failed' || job?.status === 'cancelled'
    ? '自动开通遇到问题，请联系支持；账户权益会保留。'
    : job?.status === 'confirmed'
    ? '节点账号已创建，正在确认额度下发；确认后即可导入。'
    : job && !job.enabled
    ? '开通申请已保存，自动开户暂未启用，请联系支持。'
    : job?.status === 'leased'
    ? '节点正在创建加速账号，请稍后刷新开通进度。'
    : job?.status === 'pending'
    ? '账户已进入自动开通队列，节点每 10 分钟处理一次，请稍后刷新。'
    : '暂未绑定节点客户端，请联系管理员开通。';

  if (subscription?.connectionMode === 'runtime' && !loading && !error) {
    return <section className="subscription-card" aria-labelledby="subscription-title">
      <div className="subscription-heading"><h2 id="subscription-title">{t('使用云流 App 加速')}</h2><p>{t('点击加速后自动匹配正式节点，无需手动开通或选择授权槽位。')}</p></div>
      <p role="status">{t(subscription.runtimeEnabled ? '请在 App 登录当前账户，点击加速即可申请连接。' : '正式节点服务尚未开放，账户权益会保留。')}</p>
      <button onClick={()=>void refresh()}>{t('刷新状态')}</button>
    </section>;
  }
  return (
    <section className="subscription-card" aria-labelledby="subscription-title">
      <div className="subscription-heading">
        <h2 id="subscription-title">{t("一键导入 (iOS)")}</h2>
        <p>{t("使用客户端连接云流，无需逐项填写节点配置。")}</p>
      </div>

      {loading ? (
        <p role="status">{t("正在获取订阅…")}</p>
      ) : (
        <>
          <div className="subscription-summary">
            <div>
              <b>{subscription?.nodeName || t("云流加速订阅")}</b>
              <span className={"subscription-status " + (subscription?.status ?? "pending")}>
                {subscription ? t(labels[subscription.status]) : t("暂不可用")}
              </span>
            </div>
            <dl>
              <div>
                <dt>{t("节点已用流量")}</dt>
                <dd>{subscription ? gb(used) : "—"}</dd>
              </div>
              <div>
                <dt>{t("节点总额度")}</dt>
                <dd>{subscription?.totalBytes == null ? "—" : subscription.totalBytes === 0 ? t("不限流量") : gb(subscription.totalBytes)}</dd>
              </div>
              <div>
                <dt>{t("有效期")}</dt>
                <dd>{subscription?.expiresAt ? date(subscription.expiresAt, locale) : subscription?.status === "ready" ? t("未设置到期时间") : "—"}</dd>
              </div>
            </dl>
            {subscription?.totalBytes ? (
              <progress
                value={Math.min(used, subscription.totalBytes)}
                max={subscription.totalBytes}
                aria-label={t("节点已用流量比例")}
              />
            ) : null}
          </div>

          {error && <p className="subscription-error" role="alert">{t(error)}</p>}
          {subscription?.status === "pending" && (
            <div><p className="subscription-note" role="status">{t(pendingCopy)}</p>
              <button className="account-subtle-button" type="button" onClick={() => void refresh()}><RefreshCw size={16} />{t('刷新开通进度')}</button>
            </div>
          )}

          {canImport && subscription?.url && (
            <div className="subscription-actions">
              <a
                className="button"
                href={clashImportUrl(subscription.url)}
                rel="noreferrer"
                onClick={() => setNotice("请在浏览器弹窗中允许打开客户端。导入后选中云流订阅，开启系统代理；未弹出时可复制链接手动导入。")}
              >
                {t("一键导入 (iOS)")} <ExternalLink size={16} />
              </a>
              <button type="button" onClick={() => void copy()}>
                <Copy size={16} />
                {t("复制订阅链接")}
              </button>
              <a href={subscription.url} download="flux-cloud.yaml" rel="noreferrer">
                <Download size={16} />
                {t("下载配置")}
              </a>
            </div>
          )}

          {manualCopy && subscription?.url && (
            <input
              className="subscription-copy-input"
              aria-label={t("私人订阅链接，勿分享")}
              readOnly
              value={subscription.url}
              onFocus={(event) => event.currentTarget.select()}
            />
          )}
          {notice && <p className="subscription-notice" role="status">{t(notice)}</p>}
        </>
      )}
    </section>
  );
}
